import { DEBUG, HUD } from '../config/tuning';
import type { Loop } from '../core/Loop';
import type { Registry } from '../core/Registry';
import { allPools } from '../core/Pool';
import { bus } from '../core/EventBus';
import type { Renderer } from '../render/Renderer';
import type { PostStack } from '../render/PostStack';
import type { Hud } from '../hud/Hud';
import type { TerrainRenderer } from '../world/TerrainRenderer';
import { formatSelfTestResult, runDeterminismSelfTest } from './DeterminismSelfTest';

/**
 * Debug overlay — SPEC §4.6. Toggled with `~`.
 *
 * Built at M0 rather than "when it is needed", because it is what makes every later milestone
 * debuggable: the frame-time graph localises hitches, the checksum readout localises
 * non-determinism, and pause/step/slow-motion are the only practical way to inspect a terminal
 * intercept at M6.
 *
 * The text is rebuilt at 5 Hz, not per frame: string construction allocates, and the overlay
 * must not be the thing that causes the GC pauses it exists to diagnose. The graph, which draws
 * with no allocation, updates every frame.
 */

export interface DebugOverlayContext {
  loop: Loop;
  registry: Registry;
  renderer: Renderer;
  post: PostStack;
  hud: Hud;
  terrainRenderer: TerrainRenderer;
  /** Current simulation-state checksum (uint32). */
  checksum: () => number;
  /** Seed the simulation is running from. */
  seed: number;
  /** Restart the simulation from tick 0. */
  reset: () => void;
}

const OVERLAY_STYLE = `
  position: fixed; top: 12px; left: 12px; z-index: 10;
  font: 11px/1.45 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.04em; text-transform: uppercase;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.72);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 8px 10px; min-width: 260px; pointer-events: none;
  text-shadow: 0 0 6px rgba(63, 232, 216, 0.35);
`;

export class DebugOverlay {
  private readonly context: DebugOverlayContext;
  private readonly root: HTMLDivElement;
  private readonly lines: HTMLDivElement[] = [];
  private readonly graphCanvas: HTMLCanvasElement;
  private readonly graphContext: CanvasRenderingContext2D | null;

  /** Rolling windows, milliseconds, sharing one cursor. */
  private readonly frameSamples = new Float32Array(DEBUG.FRAME_GRAPH_SAMPLES);
  private readonly simSamples = new Float32Array(DEBUG.FRAME_GRAPH_SAMPLES);
  private readonly renderSamples = new Float32Array(DEBUG.FRAME_GRAPH_SAMPLES);
  private readonly hudSamples = new Float32Array(DEBUG.FRAME_GRAPH_SAMPLES);
  private readonly postSamples = new Float32Array(DEBUG.FRAME_GRAPH_SAMPLES);
  private sampleCursor = 0;
  private sampleCount = 0;

  private visible = false;
  private lastTextUpdateMs = 0;
  private selfTestLine = 'SELFTEST  [Y] TO RUN';

  constructor(context: DebugOverlayContext, parent: HTMLElement = document.body) {
    this.context = context;

    this.root = document.createElement('div');
    this.root.setAttribute('style', OVERLAY_STYLE);

    const title = document.createElement('div');
    title.textContent = 'VECTOR ZERO · DEBUG';
    title.style.opacity = '0.65';
    title.style.marginBottom = '4px';
    this.root.appendChild(title);

    for (let i = 0; i < 9; i += 1) {
      const line = document.createElement('div');
      this.lines.push(line);
      this.root.appendChild(line);
    }

    this.graphCanvas = document.createElement('canvas');
    this.graphCanvas.width = DEBUG.GRAPH_WIDTH_PX;
    this.graphCanvas.height = DEBUG.GRAPH_HEIGHT_PX;
    this.graphCanvas.style.display = 'block';
    this.graphCanvas.style.marginTop = '6px';
    this.graphCanvas.style.width = `${DEBUG.GRAPH_WIDTH_PX}px`;
    this.graphCanvas.style.height = `${DEBUG.GRAPH_HEIGHT_PX}px`;
    this.root.appendChild(this.graphCanvas);
    this.graphContext = this.graphCanvas.getContext('2d');

    const help = document.createElement('div');
    help.textContent = '~ HIDE · P PAUSE · . STEP · T TIME · R RESET · Y SELFTEST';
    help.style.opacity = '0.5';
    help.style.marginTop = '6px';
    this.root.appendChild(help);

    this.root.style.display = 'none';
    parent.appendChild(this.root);

    window.addEventListener('keydown', this.onKeyDown);
  }

  get isVisible(): boolean {
    return this.visible;
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return;
    this.visible = visible;
    this.root.style.display = visible ? 'block' : 'none';
    bus.emit('debug:visibility', { visible });
  }

  toggle(): void {
    this.setVisible(!this.visible);
  }

  /**
   * Called once per rendered frame. Samples the frame time always (so the graph is already
   * populated when the overlay is first shown) but only draws when visible.
   */
  update(): void {
    const loop = this.context.loop;
    this.frameSamples[this.sampleCursor] = loop.frameMs;
    this.simSamples[this.sampleCursor] = loop.simMs;
    this.renderSamples[this.sampleCursor] = loop.renderMs;
    this.hudSamples[this.sampleCursor] = this.context.hud.lastDrawMs;
    this.postSamples[this.sampleCursor] = this.context.post.lastDrawMs;
    this.sampleCursor = (this.sampleCursor + 1) % this.frameSamples.length;
    if (this.sampleCount < this.frameSamples.length) this.sampleCount += 1;

    if (!this.visible) return;

    this.drawGraph();

    const nowMs = performance.now();
    if (nowMs - this.lastTextUpdateMs < 1000 / DEBUG.TEXT_UPDATE_HZ) return;
    this.lastTextUpdateMs = nowMs;
    this.updateText();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
  }

  /**
   * Mean of a rolling window, milliseconds.
   *
   * Averaged rather than instantaneous because browsers clamp `performance.now()` to ~100 µs
   * without cross-origin isolation: a single frame's sim time reads as 0.0 or 0.1 ms and tells
   * you nothing, while the mean over 120 frames resolves to a useful figure.
   */
  private averageOf(samples: Float32Array): number {
    if (this.sampleCount === 0) return 0;
    let total = 0;
    for (let i = 0; i < this.sampleCount; i += 1) total += samples[i];
    return total / this.sampleCount;
  }

  /** 95th-percentile-ish worst frame in the window, milliseconds. */
  private worstFrameMs(): number {
    let worst = 0;
    for (let i = 0; i < this.sampleCount; i += 1) {
      if (this.frameSamples[i] > worst) worst = this.frameSamples[i];
    }
    return worst;
  }

  private updateText(): void {
    const { loop, registry, renderer, checksum, seed } = this.context;
    const averageMs = this.averageOf(this.frameSamples);
    const fps = averageMs > 0 ? 1000 / averageMs : 0;
    const renderInfo = renderer.info.render;

    const state = loop.isPaused ? 'PAUSED' : 'RUNNING';
    const checksumHex = checksum().toString(16).toUpperCase().padStart(8, '0');

    this.lines[0].textContent = `FPS ${fps.toFixed(1).padStart(5)}   FRAME ${averageMs.toFixed(2)}MS   PEAK ${this.worstFrameMs().toFixed(1)}MS`;
    // HUD budget is 1.0 ms of the 16.6 ms frame (SPEC §9), so it is shown against that number
    // rather than buried in the render total it is part of.
    this.lines[1].textContent = `SIM ${this.averageOf(this.simSamples).toFixed(2)}MS   RENDER ${this.averageOf(this.renderSamples).toFixed(2)}MS   HUD ${this.averageOf(this.hudSamples).toFixed(2)}MS`;
    this.lines[2].textContent = `TICK ${loop.time.tick}   T+${loop.time.simTimeS.toFixed(2)}S   ALPHA ${loop.alpha.toFixed(3)}`;
    this.lines[3].textContent = `${state}   SCALE ${loop.timeScale.toFixed(2)}X   RES ${renderer.scale.toFixed(2)}   TICKS/F ${loop.ticksLastFrame}`;
    this.lines[4].textContent = `ENT ${registry.count}  DRAWS ${renderInfo.calls}  TRIS ${(renderInfo.triangles / 1000).toFixed(0)}K  PATCH ${this.context.terrainRenderer.patchCount}`;
    this.lines[5].textContent = `POOLS ${this.formatPools()}`;
    // The downlink line. Link quality is the number M5 and M8 drive and everything in the post
    // stack keys off (SPEC §3.3.2 item 6), so it is on the overlay from the day it exists.
    //
    // Averaged over the same window as SIM/RENDER/HUD rather than reported instantaneously: an
    // occasional slow frame would otherwise be whatever the 5 Hz text refresh happened to land
    // on, which reads as a cost the stack is paying every frame when it is not. Peaks are still
    // visible — they are in the frame-time graph below, where a spike belongs.
    const post = this.context.post;
    const stops = post.exposureStops;
    this.lines[6].textContent =
      `POST ${this.averageOf(this.postSamples).toFixed(2)}MS  ${post.passCount} PASS  ` +
      `LINK ${post.linkQuality.toFixed(2)}  GAIN ${stops >= 0 ? '+' : '−'}${Math.abs(stops).toFixed(1)}EV`;
    this.lines[7].textContent = `SEED 0x${seed.toString(16).toUpperCase()}   SUM 0x${checksumHex}`;
    this.lines[8].textContent = this.selfTestLine;
  }

  private formatPools(): string {
    const pools = allPools();
    if (pools.length === 0) return 'NONE';
    let text = '';
    for (let i = 0; i < pools.length; i += 1) {
      const pool = pools[i];
      if (i > 0) text += '  ';
      text += `${pool.name} ${pool.inUse}/${pool.capacity} (HW ${pool.highWaterMark})`;
    }
    return text;
  }

  /** Draw the rolling frame-time graph. Allocation-free. */
  private drawGraph(): void {
    const ctx = this.graphContext;
    if (ctx === null) return;

    const width = DEBUG.GRAPH_WIDTH_PX;
    const height = DEBUG.GRAPH_HEIGHT_PX;
    const ceiling = DEBUG.GRAPH_CEILING_MS;
    const barWidth = width / this.frameSamples.length;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(4, 12, 14, 0.6)';
    ctx.fillRect(0, 0, width, height);

    // 60 fps budget line.
    const budgetY = height - (DEBUG.BUDGET_LINE_MS / ceiling) * height;
    ctx.strokeStyle = 'rgba(255, 176, 0, 0.55)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, budgetY + 0.5);
    ctx.lineTo(width, budgetY + 0.5);
    ctx.stroke();

    // Oldest sample on the left: walk the ring from the write cursor.
    for (let i = 0; i < this.sampleCount; i += 1) {
      const index = (this.sampleCursor + i) % this.frameSamples.length;
      const sampleMs = this.frameSamples[index];
      const clamped = sampleMs > ceiling ? ceiling : sampleMs;
      const barHeight = (clamped / ceiling) * height;
      ctx.fillStyle =
        sampleMs > DEBUG.BUDGET_LINE_MS ? 'rgba(255, 92, 60, 0.9)' : 'rgba(63, 232, 216, 0.75)';
      ctx.fillRect(i * barWidth, height - barHeight, barWidth, barHeight);
    }
  }

  /** Bound once — a per-event closure would be re-created on every key press. */
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    const loop = this.context.loop;

    switch (event.code) {
      case 'Backquote':
        this.toggle();
        event.preventDefault();
        break;
      case 'KeyP':
        loop.togglePause();
        break;
      case 'Period':
        loop.step(event.shiftKey ? 10 : 1);
        break;
      case 'KeyT':
        loop.cycleTimeScale();
        break;
      case 'KeyR':
        this.context.reset();
        break;
      case 'KeyY': {
        const result = runDeterminismSelfTest(this.context.seed);
        this.selfTestLine = formatSelfTestResult(result);
        // Console is the right place for this: it is a pass/fail fact, not a live readout.
        console.info(this.selfTestLine);
        break;
      }
      default:
        break;
    }
  };
}
